import { useMemo, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../lib/auth-context.js";
import { listBills } from "../../lib/api-modules/bills.js";
import { listIncomeEvents } from "../../lib/api-modules/income-events.js";
import { listSpendingTransactions } from "../../lib/api-modules/spending-transactions.js";
import {
  updateFireAssumptions,
  type UpdateFireAssumptionsInput,
} from "../../lib/api-modules/households.js";
import {
  baristaFireCrossover,
  coastFireCrossover,
  fireNumbers,
  savingsRate,
  timeToFI,
} from "../../lib/util/fire.js";
import {
  accountTaxMix,
  estimateRetirementTaxRatePct,
  grossUpForTax,
} from "../../lib/util/retirement-plan.js";
import { centsToDollars, dollarsToCents, formatCentsCompact } from "../../lib/util/money.js";
import { useToast } from "../toast.js";
import {
  Button,
  Card,
  NumberField,
  Pill,
} from "../design-system/index.js";

/**
 * Phase 6 Session 1 — the FIRE marketing center of gravity.
 *
 * Replaces the locked "FIRE numbers — Coming in Phase 6" placeholder
 * Card on /dashboard. Renders:
 *   - Hero savings rate (trailing 12 months).
 *   - 5 FIRE number tiles: Lean / Coast / Barista / Regular / Fat.
 *   - Time-to-FI line: "X years to Regular FI; cross on YYYY-MM".
 *   - Inline settings disclosure for the per-household assumption block
 *     (withdrawal rate, real return, multipliers, birth year,
 *     retirement age, optional spend + barista income overrides).
 *
 * Privacy mode masks $ values; the savings-rate percent stays visible
 * (it's a ratio, not absolute net worth).
 *
 * Empty states:
 *   - No income in trailing 12 months → "Add income events" CTA.
 *   - birth_year_primary unset → Coast tile shows "set birth year" hint.
 *   - barista_annual_income_cents unset → Barista tile shows "set part-time income".
 */

const DEFAULT_WITHDRAWAL_RATE_PCT = 4;
const DEFAULT_EXPECTED_RETURN_RATE_PCT = 5;
const DEFAULT_LEAN_MULTIPLIER = 0.7;
const DEFAULT_FAT_MULTIPLIER = 2.5;
const DEFAULT_RETIREMENT_AGE = 65;

interface FireTileProps {
  readonly hideValues: boolean;
  readonly fiAssetsCents: number;
  readonly byAccountType?: Readonly<Record<string, number>> | undefined;
}

export function FireTile({ hideValues, fiAssetsCents, byAccountType }: FireTileProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();

  // === data fetch ============================================================
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const trailingFrom = useMemo(() => {
    // Last 12 complete months ending the day before today.
    const d = new Date(today);
    d.setUTCMonth(d.getUTCMonth() - 12);
    return d.toISOString().slice(0, 10);
  }, [today]);

  const billsQuery = useQuery({
    queryKey: ["bills", { includeArchived: false }],
    queryFn: () => listBills(false),
  });
  const incomeQuery = useQuery({
    queryKey: ["income-events", { from: trailingFrom }],
    queryFn: () => listIncomeEvents({ from: trailingFrom }),
  });
  const spendQuery = useQuery({
    queryKey: ["spending-transactions", { from: trailingFrom, to: today }],
    queryFn: () =>
      listSpendingTransactions({ from: trailingFrom, to: today }),
  });

  // === assumptions (with defaults) ==========================================
  const assumptions: AssumptionsResolved = useMemo(
    () => ({
      withdrawal_rate_pct:
        user?.fire_assumptions?.withdrawal_rate_pct ?? DEFAULT_WITHDRAWAL_RATE_PCT,
      expected_return_rate_pct:
        user?.fire_assumptions?.expected_return_rate_pct ??
        DEFAULT_EXPECTED_RETURN_RATE_PCT,
      lean_spend_multiplier:
        user?.fire_assumptions?.lean_spend_multiplier ?? DEFAULT_LEAN_MULTIPLIER,
      fat_spend_multiplier:
        user?.fire_assumptions?.fat_spend_multiplier ?? DEFAULT_FAT_MULTIPLIER,
      retirement_target_age:
        user?.fire_assumptions?.retirement_target_age ?? DEFAULT_RETIREMENT_AGE,
      birth_year_primary: user?.fire_assumptions?.birth_year_primary,
      birth_year_spouse: user?.fire_assumptions?.birth_year_spouse,
      social_security_pia_spouse_cents:
        user?.fire_assumptions?.social_security_pia_spouse_cents,
      social_security_claim_age_spouse:
        user?.fire_assumptions?.social_security_claim_age_spouse,
      pre_medicare_healthcare_annual_cents:
        user?.fire_assumptions?.pre_medicare_healthcare_annual_cents,
      annual_spend_override_cents:
        user?.fire_assumptions?.annual_spend_override_cents,
      barista_annual_income_cents:
        user?.fire_assumptions?.barista_annual_income_cents,
    }),
    [user?.fire_assumptions],
  );

  // === computations ==========================================================
  const isLoading =
    billsQuery.isLoading || incomeQuery.isLoading || spendQuery.isLoading;
  const isError =
    billsQuery.isError || incomeQuery.isError || spendQuery.isError;

  const computed = useMemo(() => {
    if (isLoading || isError) return null;
    const sr = savingsRate({
      income_events: incomeQuery.data?.income_events ?? [],
      bills: billsQuery.data?.bills ?? [],
      spending_transactions: spendQuery.data?.spending_transactions ?? [],
      today,
      lookback_months: 12,
    });
    const annualSpendCents =
      assumptions.annual_spend_override_cents ?? sr.annualized_spend_cents;
    // The FI number has to cover tax on withdrawals, not just spending.
    const taxRatePct = estimateRetirementTaxRatePct({
      annual_spend_cents: annualSpendCents,
      mix: accountTaxMix(byAccountType),
    });
    const grossSpendCents = grossUpForTax(annualSpendCents, taxRatePct);
    const numbers = fireNumbers({
      annual_spend_cents: grossSpendCents,
      withdrawal_rate_pct: assumptions.withdrawal_rate_pct,
      lean_spend_multiplier: assumptions.lean_spend_multiplier,
      fat_spend_multiplier: assumptions.fat_spend_multiplier,
    });
    const tFI = timeToFI({
      current_net_worth_cents: fiAssetsCents,
      monthly_net_savings_cents: sr.monthly_net_savings_cents,
      annual_return_rate_pct: assumptions.expected_return_rate_pct,
      fire_target_cents: numbers.regular_cents,
      today,
    });
    const coast =
      assumptions.birth_year_primary !== undefined
        ? coastFireCrossover({
            current_net_worth_cents: fiAssetsCents,
            monthly_net_savings_cents: sr.monthly_net_savings_cents,
            annual_return_rate_pct: assumptions.expected_return_rate_pct,
            fire_target_cents: numbers.regular_cents,
            current_age:
              new Date(today).getUTCFullYear() -
              assumptions.birth_year_primary,
            retirement_target_age: assumptions.retirement_target_age,
            today,
          })
        : null;
    const barista =
      assumptions.barista_annual_income_cents !== undefined
        ? baristaFireCrossover({
            current_net_worth_cents: fiAssetsCents,
            monthly_net_savings_cents: sr.monthly_net_savings_cents,
            annual_return_rate_pct: assumptions.expected_return_rate_pct,
            annual_spend_cents: grossSpendCents,
            barista_annual_income_cents: assumptions.barista_annual_income_cents,
            withdrawal_rate_pct: assumptions.withdrawal_rate_pct,
            today,
          })
        : null;
    return {
      savingsRate: sr,
      annualSpendCents,
      taxRatePct,
      numbers,
      timeToFI: tFI,
      coast,
      barista,
    };
  }, [
    billsQuery.data,
    incomeQuery.data,
    spendQuery.data,
    today,
    isLoading,
    isError,
    assumptions,
    fiAssetsCents,
    byAccountType,
  ]);

  const hasIncome =
    !isLoading && (computed?.savingsRate.gross_income_cents ?? 0) > 0;
  // hasFireTarget: when annual spend resolves to 0 (no bills, no
  // transactions, no override), the FI target is 0 × multiple = 0 and
  // any positive net worth would otherwise read as "past FI". Block the
  // body render so the empty-state CTA fires instead.
  const hasFireTarget =
    !isLoading && (computed?.numbers.regular_cents ?? 0) > 0;
  const showEmptyState = !isLoading && (!hasIncome || !hasFireTarget);

  // === settings form =========================================================
  const [expanded, setExpanded] = useState(false);
  const mutation = useMutation({
    mutationFn: (input: UpdateFireAssumptionsInput) => {
      if (!user) throw new Error("Not signed in");
      return updateFireAssumptions(user.primary_household_id, input);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
      toast.show("FIRE settings saved.");
      setExpanded(false);
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : "Failed to save";
      toast.show(msg);
    },
  });

  // === render ================================================================
  return (
    <Card className="mb-6" elevated>
      <div className="mb-4 flex items-baseline justify-between gap-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-[var(--color-muted)]">
            FIRE numbers
          </p>
          <p className="mt-0.5 text-xs text-[var(--color-muted)]">
            Trailing 12-month savings rate + your path to FI at{" "}
            {assumptions.withdrawal_rate_pct}% withdrawal,{" "}
            {assumptions.expected_return_rate_pct}% real return
            {computed ? `, about ${computed.taxRatePct.toFixed(0)}% tax` : ""}.
            Counts retirement assets only, not 529 or UTMA accounts.
          </p>
        </div>
        <Button
          variant="ghost"
          size="xs"
          onClick={() => setExpanded((e) => !e)}
          aria-expanded={expanded}
          aria-controls="fire-settings-panel"
          data-testid="fire-settings-toggle"
        >
          {expanded ? "Close settings" : "Settings"}
        </Button>
      </div>

      {isLoading ? (
        <p className="py-8 text-center text-sm text-[var(--color-muted)]" data-testid="fire-loading">
          Loading…
        </p>
      ) : isError ? (
        <p
          className="py-8 text-center text-sm text-[var(--color-danger)]"
          data-testid="fire-error"
        >
          Couldn't load FIRE inputs.
        </p>
      ) : showEmptyState ? (
        <div
          className="rounded-md border border-dashed border-[var(--color-border-default)] p-8 text-center"
          data-testid="fire-empty"
        >
          <p className="font-medium text-[var(--color-text)]">
            {!hasIncome
              ? "Add income events to see your FIRE numbers."
              : "Add spending to calculate your FI target."}
          </p>
          <p className="mt-1 text-sm text-[var(--color-muted)]">
            {!hasIncome
              ? "Hearth needs at least one paycheck or vest event in the last 12 months to compute your savings rate."
              : "Hearth needs trailing-12-month bills + transactions (or an annual-spend override below) to compute your FI number."}
          </p>
        </div>
      ) : (
        computed && (
          <FireBody
            computed={computed}
            assumptions={assumptions}
            hideValues={hideValues}
          />
        )
      )}

      {expanded && (
        <SettingsPanel
          initial={assumptions}
          mutation={mutation}
          onClose={() => setExpanded(false)}
        />
      )}
    </Card>
  );
}

// ============================================================================
// Body — savings-rate hero + 5 FIRE tiles + Time-to-FI line
// ============================================================================

interface FireComputation {
  savingsRate: ReturnType<typeof savingsRate>;
  annualSpendCents: number;
  numbers: ReturnType<typeof fireNumbers>;
  timeToFI: ReturnType<typeof timeToFI>;
  coast: ReturnType<typeof coastFireCrossover> | null;
  barista: ReturnType<typeof baristaFireCrossover> | null;
}

interface FireBodyProps {
  readonly computed: FireComputation;
  readonly assumptions: AssumptionsResolved;
  readonly hideValues: boolean;
}

interface AssumptionsResolved {
  withdrawal_rate_pct: number;
  expected_return_rate_pct: number;
  lean_spend_multiplier: number;
  fat_spend_multiplier: number;
  retirement_target_age: number;
  birth_year_primary: number | undefined;
  birth_year_spouse: number | undefined;
  social_security_pia_spouse_cents: number | undefined;
  social_security_claim_age_spouse: number | undefined;
  pre_medicare_healthcare_annual_cents: number | undefined;
  annual_spend_override_cents: number | undefined;
  barista_annual_income_cents: number | undefined;
}

function FireBody({ computed, assumptions, hideValues }: FireBodyProps) {
  const rate = computed.savingsRate.savings_rate_pct;
  const ratePill: "accent" | "success" | "neutral" | "danger" =
    rate < 0
      ? "danger"
      : rate >= 25
        ? "accent"
        : rate >= 15
          ? "success"
          : "neutral";

  return (
    <div data-testid="fire-body">
      {/* Hero savings rate */}
      <div className="mb-5 flex flex-wrap items-baseline gap-3">
        <p
          className="font-display text-5xl font-medium tracking-tight tabular-nums text-[var(--color-text)]"
          data-testid="fire-savings-rate"
        >
          {Math.round(rate)}%
        </p>
        <Pill tone={ratePill}>savings rate</Pill>
        <p className="text-sm text-[var(--color-muted)]">
          on{" "}
          <span className="font-mono tabular-nums">
            {formatCentsCompact(computed.savingsRate.net_income_cents, {
              hidden: hideValues,
            })}
          </span>{" "}
          income
        </p>
      </div>

      {/* 5 FIRE number tiles */}
      <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <FireNumberTile
          label="Lean FI"
          target={computed.numbers.lean_cents}
          tooltip={`Lean spend = ${assumptions.lean_spend_multiplier}× current. ${(100 / assumptions.withdrawal_rate_pct).toFixed(1)}× target.`}
          hideValues={hideValues}
        />
        <FireNumberTile
          label="Coast FIRE"
          target={computed.coast?.coast_target_today_cents ?? null}
          tooltip="Net worth needed today such that growth alone reaches Regular FI by retirement age (no further savings)."
          hideValues={hideValues}
          dateLabel={
            assumptions.birth_year_primary === undefined
              ? "Set birth year ↓"
              : computed.coast?.already_coast
                ? "Past Coast"
                : computed.coast?.coast_date
                  ? `~${computed.coast.coast_date.slice(0, 7)}`
                  : "Beyond horizon"
          }
        />
        <FireNumberTile
          label="Barista FIRE"
          target={computed.barista?.barista_target_cents ?? null}
          tooltip="Portfolio target where part-time income covers the gap to full spend at the assumed withdrawal rate."
          hideValues={hideValues}
          dateLabel={
            assumptions.barista_annual_income_cents === undefined
              ? "Set part-time ↓"
              : computed.barista?.already_barista
                ? "Past Barista"
                : computed.barista?.barista_date
                  ? `~${computed.barista.barista_date.slice(0, 7)}`
                  : "Beyond horizon"
          }
        />
        <FireNumberTile
          label="Regular FI"
          target={computed.numbers.regular_cents}
          tooltip={`${(100 / assumptions.withdrawal_rate_pct).toFixed(1)}× annual spend at ${assumptions.withdrawal_rate_pct}% withdrawal.`}
          hideValues={hideValues}
          dateLabel={
            computed.timeToFI.already_fi
              ? "Past FI"
              : computed.timeToFI.fi_date
                ? `~${computed.timeToFI.fi_date.slice(0, 7)}`
                : "Beyond horizon"
          }
        />
        <FireNumberTile
          label="Fat FIRE"
          target={computed.numbers.fat_cents}
          tooltip={`Fat spend = ${assumptions.fat_spend_multiplier}× current. ${(100 / assumptions.withdrawal_rate_pct * assumptions.fat_spend_multiplier).toFixed(1)}× target.`}
          hideValues={hideValues}
        />
      </div>

      {/* Time-to-FI summary */}
      <div className="rounded-md bg-[var(--color-accent-soft)] px-4 py-3 text-sm text-[var(--color-accent)]" data-testid="fire-summary">
        {computed.timeToFI.already_fi ? (
          <>
            <strong>You're past Regular FI today.</strong> Net worth ≥{" "}
            {formatCentsCompact(computed.numbers.regular_cents, {
              hidden: hideValues,
            })}
            .
          </>
        ) : computed.timeToFI.years_to_fi !== null ? (
          <>
            <strong>
              {formatYears(computed.timeToFI.years_to_fi)}
            </strong>{" "}
            until Regular FI at{" "}
            <span className="font-mono tabular-nums">
              {formatCentsCompact(computed.numbers.regular_cents, {
                hidden: hideValues,
              })}
            </span>
            {computed.timeToFI.fi_date && (
              <>
                {" "}
                — projected{" "}
                <span className="font-mono tabular-nums">
                  {computed.timeToFI.fi_date.slice(0, 7)}
                </span>
              </>
            )}
            . Saving{" "}
            <span className="font-mono tabular-nums">
              {formatCentsCompact(computed.savingsRate.monthly_net_savings_cents, {
                hidden: hideValues,
              })}
            </span>
            /month.
          </>
        ) : (
          <>
            Regular FI not reached within 100 years at the current trajectory.
            Increase savings or check your assumptions.
          </>
        )}
      </div>
    </div>
  );
}

function FireNumberTile({
  label,
  target,
  tooltip,
  hideValues,
  dateLabel,
}: {
  readonly label: string;
  readonly target: number | null;
  readonly tooltip: string;
  readonly hideValues: boolean;
  readonly dateLabel?: string;
}) {
  return (
    <div
      className="rounded-md border border-[var(--color-border-subtle)] bg-[var(--color-card)] p-3"
      title={tooltip}
      data-testid={`fire-tile-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <p className="text-[10px] font-medium uppercase tracking-wide text-[var(--color-muted)]">
        {label}
      </p>
      <p className="mt-1 font-mono text-base font-medium tabular-nums text-[var(--color-text)]">
        {target === null
          ? "—"
          : formatCentsCompact(target, { hidden: hideValues })}
      </p>
      {dateLabel && (
        <p className="mt-0.5 text-[11px] text-[var(--color-muted)]">{dateLabel}</p>
      )}
    </div>
  );
}

function formatYears(years: number): string {
  if (years < 1) {
    const months = Math.max(1, Math.round(years * 12));
    return `${months} mo`;
  }
  if (years < 10) {
    const whole = Math.floor(years);
    const months = Math.round((years - whole) * 12);
    if (months === 0) return `${whole} yr`;
    return `${whole}y ${months}mo`;
  }
  return `${Math.round(years)} yr`;
}

// ============================================================================
// Settings disclosure
// ============================================================================

function SettingsPanel({
  initial,
  mutation,
  onClose,
}: {
  readonly initial: AssumptionsResolved;
  readonly mutation: ReturnType<
    typeof useMutation<unknown, Error, UpdateFireAssumptionsInput>
  >;
  readonly onClose: () => void;
}) {
  const [withdrawal, setWithdrawal] = useState(
    String(initial.withdrawal_rate_pct),
  );
  const [returnRate, setReturnRate] = useState(
    String(initial.expected_return_rate_pct),
  );
  const [leanMult, setLeanMult] = useState(String(initial.lean_spend_multiplier));
  const [fatMult, setFatMult] = useState(String(initial.fat_spend_multiplier));
  const [birthYear, setBirthYear] = useState(
    initial.birth_year_primary !== undefined
      ? String(initial.birth_year_primary)
      : "",
  );
  const [retirementAge, setRetirementAge] = useState(
    String(initial.retirement_target_age),
  );
  const [spouseBirthYear, setSpouseBirthYear] = useState(
    initial.birth_year_spouse !== undefined
      ? String(initial.birth_year_spouse)
      : "",
  );
  const [spouseBenefit, setSpouseBenefit] = useState(
    initial.social_security_pia_spouse_cents !== undefined
      ? centsToDollars(initial.social_security_pia_spouse_cents)
      : "",
  );
  const [spouseClaimAge, setSpouseClaimAge] = useState(
    initial.social_security_claim_age_spouse !== undefined
      ? String(initial.social_security_claim_age_spouse)
      : "",
  );
  const [healthcare, setHealthcare] = useState(
    initial.pre_medicare_healthcare_annual_cents !== undefined
      ? centsToDollars(initial.pre_medicare_healthcare_annual_cents)
      : "",
  );
  const [spendOverride, setSpendOverride] = useState(
    initial.annual_spend_override_cents !== undefined
      ? centsToDollars(initial.annual_spend_override_cents)
      : "",
  );
  const [baristaIncome, setBaristaIncome] = useState(
    initial.barista_annual_income_cents !== undefined
      ? centsToDollars(initial.barista_annual_income_cents)
      : "",
  );

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const input: UpdateFireAssumptionsInput = {
      withdrawal_rate_pct: Number(withdrawal),
      expected_return_rate_pct: Number(returnRate),
      lean_spend_multiplier: Number(leanMult),
      fat_spend_multiplier: Number(fatMult),
      retirement_target_age: Number(retirementAge),
      // Override fields — null clears, undefined leaves untouched.
      birth_year_primary:
        birthYear === "" ? null : Number(birthYear),
      birth_year_spouse:
        spouseBirthYear === "" ? null : Number(spouseBirthYear),
      social_security_pia_spouse_cents:
        spouseBenefit === "" ? null : dollarsToCents(spouseBenefit),
      social_security_claim_age_spouse:
        spouseClaimAge === "" ? null : Number(spouseClaimAge),
      pre_medicare_healthcare_annual_cents:
        healthcare === "" ? null : dollarsToCents(healthcare),
      annual_spend_override_cents:
        spendOverride === "" ? null : dollarsToCents(spendOverride),
      barista_annual_income_cents:
        baristaIncome === "" ? null : dollarsToCents(baristaIncome),
    };
    mutation.mutate(input);
  }

  return (
    <form
      id="fire-settings-panel"
      onSubmit={onSubmit}
      className="mt-5 rounded-md border border-[var(--color-border-subtle)] bg-[var(--color-elevated)] p-4"
      data-testid="fire-settings-panel"
    >
      <p className="mb-3 text-xs font-medium uppercase tracking-wide text-[var(--color-muted)]">
        FIRE settings
      </p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <NumberField
          label="Withdrawal rate"
          helper="% per year (Bengen rule = 4)"
          value={withdrawal}
          onChange={(e) => setWithdrawal(e.target.value)}
          step="0.1"
          min="0.1"
          max="20"
          required
        />
        <NumberField
          label="Expected return"
          helper="% real (today's-dollars)"
          value={returnRate}
          onChange={(e) => setReturnRate(e.target.value)}
          step="0.1"
          min="-20"
          max="50"
          required
        />
        <NumberField
          label="Lean multiplier"
          helper="lean spend ÷ current spend"
          value={leanMult}
          onChange={(e) => setLeanMult(e.target.value)}
          step="0.05"
          min="0.05"
          max="2"
          required
        />
        <NumberField
          label="Fat multiplier"
          helper="fat spend ÷ current spend"
          value={fatMult}
          onChange={(e) => setFatMult(e.target.value)}
          step="0.05"
          min="0.05"
          max="20"
          required
        />
        <NumberField
          label="Birth year"
          helper="for current age (Coast)"
          value={birthYear}
          onChange={(e) => setBirthYear(e.target.value)}
          min="1900"
          max="2100"
          placeholder="e.g. 1985"
        />
        <NumberField
          label="Spouse birth year"
          helper="so the plan covers you both"
          value={spouseBirthYear}
          onChange={(e) => setSpouseBirthYear(e.target.value)}
          min="1900"
          max="2100"
          placeholder="e.g. 1987"
        />
        <NumberField
          label="Spouse Social Security"
          helper="$ per year at full retirement age"
          value={spouseBenefit}
          onChange={(e) => setSpouseBenefit(e.target.value)}
          min="0"
          step="1"
        />
        <NumberField
          label="Spouse claim age"
          helper="62 to 70, blank means 67"
          value={spouseClaimAge}
          onChange={(e) => setSpouseClaimAge(e.target.value)}
          min="62"
          max="70"
          step="1"
        />
        <NumberField
          label="Healthcare before 65"
          helper="$ per year until Medicare"
          value={healthcare}
          onChange={(e) => setHealthcare(e.target.value)}
          min="0"
          step="1"
        />
        <NumberField
          label="Retirement age"
          helper="default 65"
          value={retirementAge}
          onChange={(e) => setRetirementAge(e.target.value)}
          step="1"
          min="30"
          max="99"
          required
        />
        <NumberField
          label="Annual spend override"
          helper="leave blank to use trailing-12"
          value={spendOverride}
          onChange={(e) => setSpendOverride(e.target.value)}
          step="0.01"
          min="0"
          placeholder="e.g. 80000"
        />
        <NumberField
          label="Barista income (annual)"
          helper="for Barista crossover"
          value={baristaIncome}
          onChange={(e) => setBaristaIncome(e.target.value)}
          step="0.01"
          min="0"
          placeholder="e.g. 40000"
        />
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onClose}
          disabled={mutation.isPending}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={mutation.isPending}
        >
          {mutation.isPending ? "Saving…" : "Save"}
        </Button>
      </div>
    </form>
  );
}
